import { Box, Paper } from "@mui/material";
import { DatePivotParams } from "../../../entities/date_pivot_params";
import { PivotControl } from "../../../constants/pivot_control_options";
import { PivotViewConfig } from "../../../entities/pivot_view_config";
import { exportCustomViewCSV } from "./download_component";
import { ControlsHeader } from "./controls_header";
import { FilterBy } from "../../filter_by";
import { DateHierarchy } from "../../date_hierarchy";
import { SegmentSelect } from "../../segment_select";
import { Measure } from "../../measure";
import { DatePeriodPicker } from "../../date_period_picker";
import { IncludeAtomicPurchasePlan } from "./include_atomic_purchase_plan";
import { EditModeToggle } from "./edit_mode_toggle";
import dayjs from "dayjs";
import { useAdmin } from "../../../contexts/app/provider";
import { useModal } from "../../../contexts/modal/hooks";
import { useContext, useCallback } from "react";
import { DemandWalkEditsContext } from "../../../contexts/demand_walk_edits";
import { SegmentChangeWarningContent } from "../../modals/segment_change_warning";

export const TransformedCustomViewControlsComponent = <
  T extends DatePivotParams = DatePivotParams,
>({
  viewControllerConfig,
  isLoading,
  measureColumns,
  params,
  segmentColumns,
  setParams,
  dateHierarchyColumns,
  filterColumns,
  columnSchema,
  parsedData,
  isChartView,
  setIsChartView,
  sortConfig,
  setSortConfig,
}: {
  viewControllerConfig: PivotViewConfig<T>;
  params: T;
  setParams: (params: T) => void;
  isLoading: boolean;
  dateHierarchyColumns: {
    name: string;
    caption: string;
    min: string;
    max: string;
  }[];
  segmentColumns: { name: string; caption: string }[];
  measureColumns: { name: string; caption: string }[];
  filterColumns: { name: string; caption: string; unique_values: string[] }[];
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
      value_counts?: Record<string, number>;
    }
  >;
  parsedData?: any;
  isChartView?: boolean;
  setIsChartView?: React.Dispatch<React.SetStateAction<boolean>>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
}) => {
  const { isAdmin } = useAdmin();
  const { openModal, closeModal } = useModal();
  const {
    editMode,
    plannerOverrideInputs,
    finalForecastInputs,
    discardChanges,
    setEditMode,
  } = useContext(DemandWalkEditsContext);

  const {
    title,
    showControls,
    excludedFilterColumnFilter,
    datasetName,
    transformId,
  } = viewControllerConfig;
  // Ensure params.filters is always an array
  const safeFilters = Array.isArray(params.filters) ? params.filters : [];
  const userFilters = safeFilters.filter(
    (filter) => !excludedFilterColumnFilter.includes(filter[0])
  );
  const userFilterColumns = filterColumns.filter(
    (filter) => !excludedFilterColumnFilter.includes(filter.name)
  );

  // Show segment change warning modal
  const showSegmentChangeWarning = useCallback(
    (newSegments: string[], oldSegments: string[]) => {
      const handleConfirm = () => {
        discardChanges();
        setParams({ ...params, segments: newSegments });
        setEditMode(true);
        closeModal();
      };

      const handleCancel = () => {
        closeModal();
      };

      openModal({
        disableStepViewer: true,
        maxWidth: "sm",
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        steps: [
          {
            id: "warning",
            title: "Segment Change Warning",
            content: (
              <SegmentChangeWarningContent
                oldSegments={oldSegments}
                newSegments={newSegments}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />
            ),
            disableActions: true,
          },
        ],
      });
    },
    [discardChanges, params, setParams, openModal, closeModal, setEditMode]
  );

  // Handle segment change with edit mode check
  const handleSegmentChange = useCallback(
    (newSegments: string[]) => {
      // Check if this is a demand walk context and edit mode is active
      // Check if there are any edits to warn about
      const hasEdits =
        plannerOverrideInputs &&
        (Object.keys(plannerOverrideInputs).length > 0 ||
          Object.keys(finalForecastInputs).length > 0);

      if (editMode && hasEdits) {
        showSegmentChangeWarning(newSegments, params.segments || []);
      } else {
        // Not in demand walk edit mode, proceed normally
        setParams({ ...params, segments: newSegments });
      }
    },
    [
      editMode,
      plannerOverrideInputs,
      finalForecastInputs,
      params,
      setParams,
      showSegmentChangeWarning,
    ]
  );

  const handleDownload = () => {
    if (!parsedData) return;
    if (
      typeof (viewControllerConfig as any).customHandleDownload === "function"
    ) {
      try {
        (viewControllerConfig as any).customHandleDownload();
        return;
      } catch (_) {}
    }

    exportCustomViewCSV(
      parsedData.arrowTable,
      params ?? null,
      title,
      columnSchema,
      sortConfig.map(({ key, direction, index }) => ({
        key,
        direction,
        index,
      })),
      viewControllerConfig
    );
  };
  const CustomPanel = viewControllerConfig.customControlPanel;

  const customControlPanel = CustomPanel ? (
    <CustomPanel
      viewControllerConfig={viewControllerConfig}
      params={params}
      setParams={setParams}
      isLoading={isLoading}
      isChartView={isChartView}
      setIsChartView={setIsChartView}
      sortConfig={sortConfig}
      setSortConfig={setSortConfig}
      dateHierarchyColumns={dateHierarchyColumns}
      segmentColumns={segmentColumns}
      measureColumns={measureColumns}
      filterColumns={userFilterColumns}
      columnSchema={columnSchema}
      parsedData={parsedData}
    />
  ) : null;

  const defaultPanel = (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          p: 1,
          pb: 0,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
          {isAdmin &&
            showControls.includes(PivotControl.IncludeAtomicPurchasePlan) && (
              <IncludeAtomicPurchasePlan
                value={(params as any).includeAtomicPurchasePlan ?? false}
                onChange={(value) =>
                  setParams({
                    ...params,
                    includeAtomicPurchasePlan: value,
                  } as T)
                }
                disabled={isLoading}
              />
            )}

          {showControls.includes(PivotControl.DateHierarchy) && (
            <DateHierarchy
              isLoading={isLoading}
              sx={{ mr: 3, mb: 1 }}
              columns={dateHierarchyColumns}
              column={params.dateHierarchy}
              setColumn={(column) =>
                setParams({ ...params, dateHierarchy: column })
              }
            />
          )}
          {showControls.includes(PivotControl.SegmentSelectMulti) && (
            <SegmentSelect
              isLoading={isLoading}
              sx={{ mr: 3, mb: 1 }}
              segments={params.segments ?? []}
              setSegments={handleSegmentChange}
              columns={segmentColumns}
            />
          )}
          {showControls.includes(PivotControl.Measure) && params.measure && (
            <Measure
              sx={{ mr: 3, mb: 1 }}
              isLoading={isLoading}
              measure={params.measure}
              setMeasure={(measure) => setParams({ ...params, measure })}
              columns={measureColumns}
              disabled={editMode}
            />
          )}
          {showControls.includes(PivotControl.PostFilterByHardcodedColumns) &&
            viewControllerConfig.hardcodedColumns && (
              <FilterBy
                label="Focus Filters"
                isLoading={isLoading}
                excludedFilterColumnFilter={[]}
                columns={viewControllerConfig.hardcodedColumns}
                setViewFilters={(filters) =>
                  setParams({ ...params, postFilters: filters })
                }
                viewFilters={params.postFilters ?? []}
                sx={{ mr: 3, mb: 1 }}
              />
            )}
          {showControls.includes(PivotControl.FilterBy) &&
            userFilters.length <= 1 && (
              <FilterBy
                sx={{ mr: 3, mb: 1 }}
                isLoading={isLoading}
                excludedFilterColumnFilter={excludedFilterColumnFilter}
                viewFilters={params.filters}
                setViewFilters={(filters) => setParams({ ...params, filters })}
                columns={userFilterColumns}
              />
            )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          {showControls.includes(PivotControl.EditModeToggle) && (
            <EditModeToggle />
          )}
          {showControls.includes(PivotControl.DatePeriodPicker) && (
            <DatePeriodPicker
              isLoading={isLoading}
              dateColumn={params.dateHierarchy}
              columns={dateHierarchyColumns}
              minDate={
                dateHierarchyColumns.find(
                  (col) => col.name === params.dateHierarchy
                )?.min
                  ? dayjs(
                      dateHierarchyColumns.find(
                        (col) => col.name === params.dateHierarchy
                      )?.min
                    )
                  : null
              }
              maxDate={
                dateHierarchyColumns.find(
                  (col) => col.name === params.dateHierarchy
                )?.max
                  ? dayjs(
                      dateHierarchyColumns.find(
                        (col) => col.name === params.dateHierarchy
                      )?.max
                    )
                  : null
              }
              startDate={params.minDate ? dayjs(params.minDate) : dayjs()}
              setStartDate={(startDate) =>
                setParams({
                  ...params,
                  minDate: startDate || params.minDate,
                })
              }
              endDate={params.maxDate ? dayjs(params.maxDate) : dayjs()}
              setEndDate={(endDate) =>
                setParams({ ...params, maxDate: endDate || params.maxDate })
              }
            />
          )}
        </Box>
      </Box>

      {showControls.includes(PivotControl.FilterBy) &&
        userFilters.length > 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              p: 1,
              pt: 0,
              flexWrap: "wrap",
            }}
          >
            <FilterBy
              isLoading={isLoading}
              excludedFilterColumnFilter={excludedFilterColumnFilter}
              viewFilters={params.filters}
              setViewFilters={(filters) => setParams({ ...params, filters })}
              columns={userFilterColumns}
              sx={{ mr: 3, mb: 1 }}
            />
          </Box>
        )}
    </Box>
  );

  // Derive effective controls: for supply_walk_editable, only show Download when status is Approved
  let effectiveShowControls = showControls;
  try {
    if (
      (viewControllerConfig as any)?.transformId === "supply_walk_editable" &&
      Array.isArray(params?.filters)
    ) {
      const statusFilter = params.filters.find((f: any) => f?.[0] === "status");
      const currentStatus = statusFilter
        ? String(statusFilter[2]?.[0] || "")
        : "";
      if (currentStatus !== "Approved") {
        effectiveShowControls = showControls.filter(
          (c: any) => c !== PivotControl.Download
        );
      }
    }
  } catch (_) {}

  return (
    <Box sx={{ flexShrink: 0 }}>
      <ControlsHeader
        title={title}
        isLoading={isLoading}
        showControls={effectiveShowControls}
        params={params}
        setParams={setParams}
        datasetName={datasetName}
        transformId={transformId}
        handleDownload={handleDownload}
        isChartView={isChartView}
        setIsChartView={setIsChartView}
      />
      <Paper sx={{ borderRadius: 1 }}>
        {customControlPanel ? customControlPanel : defaultPanel}
      </Paper>
    </Box>
  );
};
